import React, { useState, useEffect, useRef, useCallback, useMemo, type ComponentProps } from 'react';

import {
  ShoppingCartOutlined,
  UserOutlined,
  FileTextOutlined,
  PaperClipOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import SelectNotFoundContent from '@components/shared/ui/SelectNotFoundContent';
import OrderAttachments from '@components/shared/orders/OrderAttachments';
import OrderItemsForm from '@components/shared/orders/OrderItemsForm';
import FormReceiverInformation from '@components/shared/orders/FormReceiverInformation';
import OrderNameField from '@components/shared/orders/OrderNameField';
import OrderOptionsCard from '@components/shared/orders/OrderOptionsCard';
import OrderItemsCard from '@components/shared/orders/OrderItemsCard';
import { usePlan } from '@contexts/operator/PlanContext';
import useSmartBack from '@hooks/shared/useSmartBack';
import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import { useOrderNameAvailability } from '@hooks/orders/useOrderNameAvailability';
import { useShippingTypeBehavior } from '@hooks/orders/useShippingTypeBehavior';
import { useItemMode, ITEM_MODE_PRECISE, type ItemMode } from '@hooks/orders/useItemMode';
import { useOrderBundles } from '@hooks/orders/useOrderBundles';
import { useInventoryAttributeDefinitions } from '@hooks/orders/useInventoryAttributeDefinitions';
import { buildOrderUpdatePayload } from '@utils/orders/buildOrderUpdatePayload';
import { inventoryService as adminInventoryService } from '@services/admin/inventoryService';
import { inventoryService as operatorInventoryService } from '@services/operator/inventoryService';
import {
  Input,
  Select,
  Button,
  Card,
  Row,
  Col,
  Space,
  message,
  Modal,
  Spin,
  Typography,
  Tag,
  Alert,
  Tooltip,
  Form,
} from 'antd';
import dayjs from 'dayjs';
import { toDayjsOrNull } from '@utils/dateTime';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type {
  ApiEnvelope,
  ShippingTypeOption,
  SalesChannelOption,
  InventoryTypeOption,
  MerchantOption,
  WarehouseOption,
  CurrencyOption,
  OrderRole,
} from '@components/shared/orders/types';

const { Option } = Select;
const { Title } = Typography;

interface OrderItemResponse {
  id?: string;
  product?: { id: string; sku: string; name: string; barcode?: string; validity_days?: number } | null;
  unknown_product?: { id: string; sku: string; name: string } | null;
  product_sku?: string | null;
  product_name?: string | null;
  inventory_type?: { id: string; name: string } | null;
  quantity: number;
  shipped_quantity?: number | null;
  manufacturing_date?: string | null;
  expiration_date?: string | null;
  batch?: string | null;
  note?: string | null;
  price?: number | null;
  identifier?: string | null;
  original_price?: number | null;
  cost?: number | null;
  custom_attributes?: Record<string, unknown> | null;
  inventory_id?: string | null;
  source_inventory?: unknown;
  [key: string]: unknown;
}

interface OrderResponseData {
  id: string;
  name: string;
  editable?: boolean;
  type?: string;
  extra_name?: string | null;
  is_platform_order?: boolean;
  ec_account?: { id?: string; display_name?: string } | null;
  fulfillments?: Array<{ id?: string; status?: string; sibling_orders?: unknown[] }>;
  merchant?: { id?: string; default_currency?: string } | null;
  warehouse?: { id?: string } | null;
  channel?: { id?: string } | null;
  shipping_type?: { id?: string; code?: string; name?: string } | null;
  country_code?: string | null;
  postal_code?: string | null;
  state?: string | null;
  city?: string | null;
  district?: string | null;
  address?: string | null;
  receiver_name?: string | null;
  phone?: string | null;
  email?: string | null;
  convenience_store_code?: string | null;
  total_amount?: string | number | null;
  currency?: string | null;
  is_cod?: boolean;
  delivery_with_pickup?: boolean;
  urgent?: boolean;
  scheduled_shipping_date?: string | null;
  delivery_date?: string | null;
  delivery_time?: string | null;
  seller_name?: string | null;
  seller_phone?: string | null;
  invoice_number?: string | null;
  note?: string | null;
  order_items?: OrderItemResponse[];
  items?: OrderItemResponse[];
  attachments?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

// Methods OrderEdit calls directly. Pass-through methods are typed by their
// consuming child component.
interface OrderServiceLike {
  get: (id: string) => Promise<ApiEnvelope<OrderResponseData>>;
  update: (id: string, payload: Record<string, unknown>) => Promise<ApiEnvelope<{ id?: string }>>;
  // operator 端才有附件功能；admin orderService 不提供，故為 optional
  createAttachment?: (orderId: string, file: File) => Promise<ApiEnvelope<{ id?: string }>>;
  deleteAttachment?: (orderId: string, attachmentId: string) => Promise<ApiEnvelope<{ id?: string }>>;
  checkOrderNameAvailability: (
    orderName: string,
  ) => Promise<{ exists: boolean; orderId: string | null; error: Error | null }>;
  suggestName: (merchantId?: string | null) => Promise<ApiEnvelope<{ name?: string }>>;
  [key: string]: unknown;
}

// Pass-through services — typed by their consuming child component.
type ContactAddressServiceLike = ComponentProps<typeof FormReceiverInformation>['contactAddressService'];
type ProductServiceLike = ComponentProps<typeof OrderItemsForm>['productService'];
type BundleServiceLike = ComponentProps<typeof OrderItemsForm>['bundleService'];

interface OrderEditProps {
  orderService: OrderServiceLike;
  productService: ProductServiceLike;
  contactAddressService: ContactAddressServiceLike;
  role?: OrderRole;
  orderId?: string;
  salesChannels?: SalesChannelOption[];
  shippingTypes?: ShippingTypeOption[];
  onReloadShippingTypes?: () => void | Promise<unknown>;
  inventoryTypes?: InventoryTypeOption[];
  merchantDataLoading?: boolean;
  currentMerchantId?: string | null;
  /** For operator role to select merchant */
  merchantOptions?: MerchantOption[];
  /** For admin role to select warehouse */
  warehouseOptions?: WarehouseOption[];
  /** Merchant's default warehouse */
  defaultWarehouseId?: string | null;
  /** Currency list for CurrencySelect */
  currencies?: CurrencyOption[];
  enabledOrderItemColumns?: string[];
  availableOrderItemColumns?: string[];
  onUpdateOrderItemColumns?: (columns: string[]) => Promise<boolean> | boolean | void;
  onOrderLoaded?: (order: OrderResponseData) => void;
  bundleService?: BundleServiceLike;
}

interface OrderEditFormItem {
  id?: string | null;
  key?: number;
  _is_unknown_product?: boolean;
  product_id: { value: string; label: string } | null;
  product_sku?: string;
  product_name?: string;
  product_validity_days?: number;
  manufacturing_date_required?: boolean;
  batch_management_required?: boolean;
  barcode?: string;
  sku?: string;
  name?: string;
  inventory_type_id: { value: string; label: string } | null;
  quantity: number;
  /** Server-side shipped count (0 when no fulfillment item ships this row).
   *  Used by ProductItemCard to lock fields and render the "已出 X/Y" badge,
   *  and by OrderItemsForm to compute group-level shipped sets for bundles. */
  shipped_quantity?: number;
  manufacturing_date: dayjs.Dayjs | null;
  expiration_date: dayjs.Dayjs | null;
  batch?: string;
  note?: string;
  price: number | null;
  identifier?: string;
  original_price: number | null;
  cost: number | null;
  custom_attributes?: Record<string, unknown>;
  inventory_id?: string | null;
  _source_inventory?: unknown;
  bundle_id?: string;
  bundle_key?: string;
  bundle_per_set_qty?: number;
  [extra: string]: unknown;
}

const OrderEdit: React.FC<OrderEditProps> = ({
  orderService,
  productService,
  contactAddressService,
  role = 'admin',
  orderId,
  salesChannels = [],
  shippingTypes = [],
  onReloadShippingTypes,
  inventoryTypes = [],
  merchantDataLoading = false,
  currentMerchantId = null,
  merchantOptions = [],
  warehouseOptions = [],
  currencies,
  enabledOrderItemColumns = [],
  availableOrderItemColumns = [],
  onUpdateOrderItemColumns,
  onOrderLoaded,
  bundleService,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const { plan } = usePlan();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [order, setOrder] = useState<OrderResponseData | null>(null);

  const { itemMode, setItemMode, handleModeChange } = useItemMode(form, {
    clearInventoryIdOnSwitchToInput: true,
  });

  // 從訂單資料偵測模式：僅當 items 已綁定 inventory_id 時強制 PRECISE（切走會丟資料）；
  // 其餘情況回傳 null，交給 localStorage 偏好 / 目前狀態決定。
  const detectMode = useCallback((orderItems: OrderItemResponse[] | undefined): ItemMode | null => {
    if (orderItems?.some(item => item.inventory_id)) return ITEM_MODE_PRECISE;
    return null;
  }, []);

  const activeInventoryService = role === 'admin' ? adminInventoryService : operatorInventoryService;

  // 平台訂單判斷：ec_account_id 存在代表透過 EC API 匯入。
  // 平台訂單可手動編輯所有欄位，但儲存前會彈出 confirm dialog 提醒
  // 變更不會自動同步回來源 EC 平台。
  const isPlatformOrder = !!(order?.is_platform_order || order?.ec_account?.id);

  const {
    showCod,
    showDeliveryWithPickup,
    handleShippingTypeChange,
    filteredShippingTypes,
  } = useShippingTypeBehavior(form, shippingTypes);

  // 監聽 order_items
  const watchedOrderItems = Form.useWatch('order_items', form) as OrderEditFormItem[] | undefined;

  // Precise mode: 任一 item 缺 inventory_id
  const preciseModeIncomplete = itemMode === ITEM_MODE_PRECISE &&
    Array.isArray(watchedOrderItems) &&
    watchedOrderItems.length > 0 &&
    watchedOrderItems.some(item => !item?.inventory_id);

  // 訂單名稱檢查狀態
  const [originalName, setOriginalName] = useState(''); // 載入後填入；hook 需 re-render 才看得到，故用 state 不用 ref
  const {
    status: nameCheckStatus,
    existingOrderId,
    onNameChange: triggerNameCheck,
  } = useOrderNameAvailability({
    checkAvailability: orderService.checkOrderNameAvailability,
    debounceMs: 500,
    excludeName: originalName,
  });
  const [suggesting, setSuggesting] = useState(false);

  const handleSuggestName = async () => {
    try {
      setSuggesting(true);
      const res = await orderService.suggestName();
      if (res.success) {
        form.setFieldsValue({ name: res.data?.name });
      } else {
        handleApiError(res, { t });
      }
    } catch (error) {
      handleNetworkError(error, { t });
    } finally {
      setSuggesting(false);
    }
  };

  const addItemRef = useRef<((item: Record<string, unknown>) => void) | null>(null);
  const removeItemRef = useRef<((index: number | number[]) => void) | null>(null);

  const handleAddItem = useCallback(() => {
    if (!addItemRef.current) return;
    addItemRef.current({
      key: Date.now(),
      product_id: null,
      sku: '',
      name: '',
      inventory_type_id: inventoryTypes[0] ? { value: inventoryTypes[0].id, label: inventoryTypes[0].name } : null,
      quantity: 1,
      manufacturing_date: null,
      expiration_date: null,
      batch: '',
      note: '',
      price: null,
      identifier: '',
      original_price: null,
      cost: null,
      custom_attributes: {},
    } as OrderEditFormItem as unknown as Record<string, unknown>);
    setTimeout(() => {
      const cards = document.querySelectorAll('.product-item-card');
      (cards[cards.length - 1] as HTMLElement | undefined)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }, [inventoryTypes]);

  const {
    bundleGroups,
    handleBundleSetQuantityChange,
    handleRemoveBundle,
    handleBundleSelectFromCard,
  } = useOrderBundles({ form, addItemRef, removeItemRef, inventoryTypes });

  const inventoryAttributeDefinitions = useInventoryAttributeDefinitions(
    role,
    order?.merchant?.id || currentMerchantId,
  );

  const [originalOrderItems, setOriginalOrderItems] = useState<OrderEditFormItem[]>([]); // 保存原始商品資料

  // 銷售通路空選項時的引導內容
  const salesChannelNotFoundContent = (
    <SelectNotFoundContent
      hint={t('shared.orders.form.no_sales_channels_hint')}
      settingsPath={role === 'operator' ? '/operator/data/sales_channels' : '/admin/sales_channels'}
      settingsLabel={t('shared.orders.form.go_to_sales_channel_settings')}
    />
  );

  // 物流方式空選項時的引導內容
  const shippingTypeNotFoundContent = role === 'operator' ? (
    <SelectNotFoundContent
      hint={t('shared.orders.form.no_shipping_types_hint')}
      settingsPath={plan === 'standard' ? '/operator/shipping_accounts' : '/operator/system_settings/merchant_shipping_types'}
      settingsLabel={plan === 'standard' ? t('shared.orders.form.go_to_shipping_account_settings') : t('shared.orders.form.go_to_shipping_type_settings')}
      onReload={onReloadShippingTypes}
    />
  ) : null;

  // 載入訂單資料
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setInitialLoading(true);
        const data = await orderService.get(orderId!);

        if (data.success && data.data) {
          const orderData = data.data;
          setOrder(orderData);
          setOriginalName(orderData.name);

          // 商品清單轉換
          let items: OrderEditFormItem[] = [];
          const rawItems = orderData.order_items || orderData.items;
          if (rawItems) {
            items = rawItems.map((item: OrderItemResponse): OrderEditFormItem => ({
              id: item.id,
              // 含 unknown_product 的 item：product_id 維持 null（DB 也是 nil），用 _is_unknown_product 旗標
              // 在 submit 時排除 product_id payload，避免把 unknown_product.id 寫成 product_id 造成資料毀損
              _is_unknown_product: !!item.unknown_product && !item.product,
              product_id: item.product ? {
                value: item.product.id,
                label: `${item.product.sku} - ${item.product.name}`
              } : (item.unknown_product ? {
                // value 用 unknown_product.id 只為了讓 antd Select 有 controlled value 可顯示 label，
                // 不會被 submit 出去（見 submitUpdate 的 _is_unknown_product 判斷）
                value: item.unknown_product.id,
                label: `${item.unknown_product.sku} - ${item.unknown_product.name}`
              } : null),
              product_sku: item.product?.sku || item.unknown_product?.sku || item.product_sku || '',
              product_name: item.product?.name || item.unknown_product?.name || item.product_name || '',
              barcode: item.product?.barcode || '',
              product_validity_days: item.product?.validity_days,
              inventory_type_id: item.inventory_type ? { value: item.inventory_type.id, label: item.inventory_type.name } : null,
              quantity: item.quantity,
              // Forward shipped_quantity so partial-shipped row affordances (locked fields,
              // min=shipped quantity, "已出 X/Y" badge, hidden remove button) read from
              // form values.
              shipped_quantity: item.shipped_quantity ?? 0,
              // Bundle group lock for already-loaded orders is intentionally not wired up:
              // the API does not return bundle_key/bundle_per_set_qty, and bundleGroups
              // state is only populated by in-session "Add Bundle" actions (see
              // useOrderBundles.handleSelectBundle). Backend L2/L3 guards still protect
              // shipped bundle components on submit. Adding rehydration on load is a
              // follow-up — see OrderEdit.test.tsx it.todo for the design notes.
              manufacturing_date: toDayjsOrNull(item.manufacturing_date),
              expiration_date: toDayjsOrNull(item.expiration_date),
              batch: item.batch || '',
              note: item.note || '',
              price: item.price ?? null,
              identifier: item.identifier ?? '',
              original_price: item.original_price ?? null,
              cost: item.cost ?? null,
              // custom_attributes: 保留原始 object（ProductItemCard 用 nested path 如 custom_attributes.color）
              // payload builder 也直接吃 Hash、不再做 JSON.stringify
              custom_attributes: item.custom_attributes && typeof item.custom_attributes === 'object' ? item.custom_attributes : {},
              inventory_id: item.inventory_id || null,
              _source_inventory: item.source_inventory || null
            }));
            setOriginalOrderItems(JSON.parse(JSON.stringify(items))); // Save for restoration logic if needed

            // 偵測三軌模式：僅當訂單已綁定 inventory 時覆蓋；否則沿用 localStorage 偏好
            const detectedMode = detectMode(rawItems);
            if (detectedMode) setItemMode(detectedMode);
          }

          // 填充表單資料
          const finalValues = {
            order_type: orderData.type || 'Order',
            name: orderData.name || '',
            extra_name: orderData.extra_name || '',
            merchant_id: orderData.merchant?.id || currentMerchantId,
            warehouse_id: orderData.warehouse?.id || null,
            sales_channel_id: orderData.channel?.id || null,
            shipping_type_id: orderData.shipping_type?.id || null,
            is_cod: orderData.is_cod || false,
            delivery_with_pickup: orderData.delivery_with_pickup || false,
            urgent: orderData.urgent || false,

            // Receiver
            country_code: orderData.country_code || '',
            postal_code: orderData.postal_code || '',
            state: orderData.state || '',
            city: orderData.city || '',
            district: orderData.district || '',
            address: orderData.address || '',
            receiver_name: orderData.receiver_name || '',
            phone: orderData.phone || '',
            email: orderData.email || '',

            // Other
            convenience_store_code: orderData.convenience_store_code || '',
            total_amount: orderData.total_amount ?? 0,
            currency: orderData.currency || orderData.merchant?.default_currency || 'TWD',
            scheduled_shipping_date: toDayjsOrNull(orderData.scheduled_shipping_date),
            delivery_date: toDayjsOrNull(orderData.delivery_date),
            delivery_time: orderData.delivery_time || null,
            seller_name: orderData.seller_name || '',
            seller_phone: orderData.seller_phone || '',
            invoice_number: orderData.invoice_number || '',
            note: orderData.note || '',

            order_items: items
          };
          form.setFieldsValue(finalValues);

          // 通知父元件訂單已載入（用於設定 merchant context 等）
          onOrderLoaded?.(orderData);

        } else {
          handleApiError(data, { t, fallbackMessage: t(`pages.orders.errors.load_failed`) });
          goBack();
        }
      } catch (error) {
        console.error('Failed to fetch order:', error);
        handleNetworkError(error, { t });
        goBack();
      } finally {
        setInitialLoading(false);
      }
    };

    if (!orderId) {
      return;
    }

    fetchOrder();
  }, [orderId, role, t, goBack, form]);

  // 附件上傳 / 刪除後重新載入訂單，刷新附件清單。
  // 附件走獨立 endpoint 即時生效，不需等訂單表單送出。
  const reloadAttachments = useCallback(async () => {
    if (!orderId) return;
    try {
      const data = await orderService.get(orderId);
      if (data.success && data.data) {
        const refreshed = data.data;
        setOrder((prev: OrderResponseData | null) =>
          prev ? { ...prev, attachments: refreshed.attachments || [] } : refreshed,
        );
      }
    } catch (error) {
      console.error('Failed to reload order attachments:', error);
    }
  }, [orderId, orderService]);

  // 構建 payload 並送出
  const submitUpdate = async (values: Record<string, unknown>) => {
    setLoading(true);
    try {
      // 既有 unknown_product items：載入時 form 的 product_id 顯示用 unknown_product.id 充當 value，
      // 但實際 DB 的 product_id 是 nil（unknown_product_id 才是真欄位）。submit 時必須排除 product_id，
      // 否則後端 update_attrs 會把假的 UUID 寫進 products FK，導致後續 fulfillment 重建/庫存分配失敗。
      const unknownProductItemIds = new Set<string>(
        originalOrderItems.filter(o => o._is_unknown_product && o.id).map(o => o.id as string)
      );

      const payload = buildOrderUpdatePayload(values, {
        role,
        unknownProductItemIds,
      });

      const data = await orderService.update(orderId!, payload as unknown as Record<string, unknown>);

      if (data.success) {
        message.success(t(`shared.orders.form.update_success`) as string);
        // 導向該筆記錄的 show 頁，避免返回快取的列表頁看不到更新內容
        // React Router basename 會自動補上 /operator 或 /admin 前綴
        const recordId = data.data?.id || orderId;
        navigate(`/orders/${recordId}`, { replace: true });
      } else {
        handleApiError(data, { t, fallbackMessage: t(`shared.orders.form.update_error`) });
      }
    } catch (error) {
      console.error('Failed to update order:', error);
      handleNetworkError(error, { t });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // 檢查訂單是否可編輯
      if (order && !order.editable) {
        message.error(t(`pages.orders.errors.not_editable`) as string);
        return;
      }

      if (nameCheckStatus === 'unavailable') {
        message.error(t(`shared.orders.form.messages.order_name_unavailable`) as string);
        return;
      }

      const doSubmit = () => submitUpdate(values);

      // 後端任何訂單編輯（無論品項異動 / 改收件人 / 改金額 / 改物流）都會走「砍光重建」路徑
      // — Phase 1 destroy active FLs → Phase 3 create_default_fulfillment + reallocate_inventories。
      // 即使對使用者看似只改一個欄位，下游 FL.id / 庫存鎖定 / picking_list 都會被重置。
      // 每次編輯都明確 confirm，避免使用者誤以為是輕量改動。
      const showFulfillmentConfirm = (onOk: () => void) => {
        Modal.confirm({
          title: t('shared.orders.form.fulfillment_rebuild_warning.title') as string,
          content: t('shared.orders.form.fulfillment_rebuild_warning.content') as string,
          okText: t('common.button.confirm') as string,
          cancelText: t('common.button.cancel') as string,
          onOk,
        });
      };

      // 平台訂單：先彈出「不會同步回來源平台」提示，再彈 rebuild 確認
      if (isPlatformOrder) {
        Modal.confirm({
          title: t('shared.orders.form.platform_order_edit_warning.title') as string,
          content: t('shared.orders.form.platform_order_edit_warning.content') as string,
          okText: t('common.button.confirm') as string,
          cancelText: t('common.button.cancel') as string,
          onOk: () => showFulfillmentConfirm(doSubmit),
        });
        return;
      }

      showFulfillmentConfirm(doSubmit);
    } catch (error) {
      console.error('Failed to update order:', error);
      const err = error as { errorFields?: Array<{ name: (string | number)[] }> };
      if (err.errorFields) {
        message.error(t(`common.message.please_check_form`) as string);
        if (err.errorFields.length > 0) {
          form.scrollToField(err.errorFields[0].name, { behavior: 'smooth', block: 'center' });
        }
      }
    }
  };

  if (initialLoading || merchantDataLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" tip={t('common.loading')} />
      </div>
    );
  }

  // 檢查訂單是否可編輯
  if (order && !order.editable) {
    const hasProcessingFulfillment = (order.fulfillments || []).some(
      (f) => f.status === 'processing'
    );
    return (
      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        <Alert
          message={t(`pages.orders.errors.not_editable_title`)}
          description={
            hasProcessingFulfillment
              ? t(`pages.orders.errors.not_editable_processing`)
              : t(`pages.orders.errors.not_editable_description`)
          }
          type="warning"
          showIcon
          action={
            <Button onClick={goBack}>
              {t('common.button.back')}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
      onFinish={handleSubmit}
      initialValues={{
        currency: 'TWD',
        order_type: 'Order'
      }}
    >
      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <Space size="middle">
            <Title level={2} style={{ margin: 0 }}>
              {t(`shared.orders.form.title_edit`)}
            </Title>
            {order && <Tag color="blue">{order.name}</Tag>}
            {isPlatformOrder && order?.ec_account?.display_name && (
              <Tag color="gold">{order.ec_account.display_name}</Tag>
            )}
          </Space>
        </div>

        <Row gutter={24}>
          {/* 左側欄位 */}
          <Col xs={24} lg={14}>
            {/* 基本資訊 */}
            <Card
              title={
                <Space>
                  <FileTextOutlined />
                  <span>{t(`shared.orders.form.sections.basic_info`)}</span>
                </Space>
              }
              style={{ marginBottom: 24 }}
            >
              {/* Operator: 選擇 Merchant */}
              {role === 'operator' && (
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      name="merchant_id"
                      label={t('operator.orders.form.merchant')}
                      rules={[{ required: true, message: t('operator.orders.form.merchant_required') as string }]}
                    >
                      <Select
                        placeholder={t('common.select.please_select')}
                        showSearch
                        optionFilterProp="children"
                        disabled
                      >
                        {merchantOptions.map((merchant) => (
                          <Option key={merchant.id} value={merchant.id}>
                            {merchant.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {/* Admin: 選擇出貨倉庫 (只有多個倉庫時才顯示) */}
              {role === 'admin' && warehouseOptions.length > 1 && (
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      name="warehouse_id"
                      label={t('models.order.warehouse')}
                    >
                      <Select
                        placeholder={t('common.select.please_select')}
                        showSearch
                        optionFilterProp="children"
                        allowClear
                      >
                        {warehouseOptions.map((warehouse) => (
                          <Option key={warehouse.id} value={warehouse.id}>
                            {warehouse.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              )}

              <Row gutter={16}>
                <Col span={12}>
                  <OrderNameField
                    role={role}
                    status={nameCheckStatus}
                    existingOrderId={existingOrderId}
                    onChange={triggerNameCheck}
                    // Suggest button only made sense when creating; the original
                    // file gated it on `!orderId`, which in edit mode is always
                    // false. Keep parity by omitting onSuggest entirely.
                    suggesting={suggesting}
                    disabled={loading}
                    size="large"
                  />
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="order_type"
                    label={t(`models.order.order_type`)}
                    rules={[{ required: true }]}
                  >
                    <Select
                      size="large"
                      style={{ width: '100%' }}
                      showSearch
                      disabled
                      filterOption={(input, option) =>
                        (((option?.children as unknown as string) ?? '')).toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      <Option value="Order">{t(`enums.order_type.normal`)}</Option>
                      <Option value="DraftOrder">{t(`enums.order_type.draft`)}</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="extra_name"
                    label={
                      <span>
                        {t(`models.order.extra_name`)}{' '}
                        <Tooltip title={t(`shared.orders.form.extra_name_tooltip`)}>
                          <span style={{ color: '#999', cursor: 'help' }}>ⓘ</span>
                        </Tooltip>
                      </span>
                    }
                  >
                    <Input placeholder={t(`models.order.extra_name`) as string} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="shipping_type_id"
                    label={t(`models.order.shipping_type`)}
                    rules={[{ required: true, message: t(`common.validation.required`) as string }]}
                  >
                    <Select
                      placeholder={t('common.select.please_select')}
                      showSearch
                      onChange={handleShippingTypeChange}
                      notFoundContent={shippingTypeNotFoundContent}
                      filterOption={(input, option) =>
                        (((option?.children as unknown as string) ?? '')).toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      {filteredShippingTypes.map((type) => (
                        <Option key={type.id} value={type.id}>
                          {t(`shipping_types.${type.code}`, { defaultValue: type.name })}
                        </Option>
                      ))}
                      {isPlatformOrder && order?.shipping_type &&
                        !filteredShippingTypes.some(type => type.id === order.shipping_type!.id) && (
                        <Option key={order.shipping_type.id} value={order.shipping_type.id}>
                          {t(`shipping_types.${order.shipping_type.code}`, { defaultValue: order.shipping_type.name })}
                        </Option>
                      )}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="sales_channel_id"
                    label={t(`models.order.sales_channel`)}
                    rules={[{ required: true, message: t(`common.validation.required`) as string }]}
                  >
                    <Select
                      placeholder={t('common.select.please_select')}
                      showSearch
                      optionFilterProp="label"
                      notFoundContent={salesChannelNotFoundContent}
                      options={salesChannels.map((channel) => ({
                        value: channel.id,
                        label: channel.name,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* 收件人資訊 */}
            <Card
              title={
                <Space>
                  <UserOutlined />
                  <span>{t(`shared.orders.form.sections.receiver_info`)}</span>
                </Space>
              }
              style={{ marginBottom: 24 }}
            >
              <FormReceiverInformation
                contactAddressService={contactAddressService}
                form={form}
                merchantId={order?.merchant?.id || currentMerchantId || undefined}
              />
            </Card>

            {/* 附件（operator 端才支援；admin orderService 無附件 endpoint） */}
            {orderService.createAttachment && (
              <Card
                title={
                  <Space>
                    <PaperClipOutlined />
                    <span>{t(`shared.orders.form.sections.attachments`)}</span>
                  </Space>
                }
                style={{ marginBottom: 24 }}
              >
                <OrderAttachments
                  attachments={order?.attachments || []}
                  orderId={orderId}
                  service={orderService}
                  onUpdate={reloadAttachments}
                />
              </Card>
            )}
          </Col>

          {/* 右側欄位 */}
          <Col xs={24} lg={10}>
            <OrderOptionsCard
              showCod={showCod}
              showDeliveryWithPickup={showDeliveryWithPickup}
              currencies={currencies}
              showScheduledShippingDateTooltip
            />
          </Col>
        </Row>

        {/* 商品清單 - 全版展開 */}
        <OrderItemsCard
          itemMode={itemMode}
          onModeChange={handleModeChange}
          required={false}
          addItemRef={addItemRef}
          removeItemRef={removeItemRef}
          form={form}
          productService={productService}
          inventoryTypes={inventoryTypes}
          inventoryAttributeDefinitions={inventoryAttributeDefinitions}
          merchantId={order?.merchant?.id || currentMerchantId || undefined}
          enabledColumns={enabledOrderItemColumns}
          availableColumns={availableOrderItemColumns}
          onUpdateColumns={onUpdateOrderItemColumns as ((columns: string[], merchantId?: string) => Promise<boolean> | boolean) | undefined}
          role={role}
          inventoryService={activeInventoryService}
          warehouseId={order?.warehouse?.id}
          bundleService={bundleService}
          onSelectBundle={handleBundleSelectFromCard}
          searchMerchantId={order?.merchant?.id || currentMerchantId || undefined}
          bundleGroups={bundleGroups}
          onBundleSetQuantityChange={handleBundleSetQuantityChange}
          onRemoveBundle={handleRemoveBundle}
          multiSelectInventory
        />

        <FloatingActionBarSpacer />
      </div>

      <FloatingActionBar
        role={role}
        size="large"
        onCancel={goBack}
        onSubmit={handleSubmit}
        loading={loading}
        disabled={preciseModeIncomplete}
        extraButtons={
          <Button
            size="large"
            icon={<PlusOutlined />}
            onClick={handleAddItem}
          >
            {t('shared.orders.form.add_item')}
          </Button>
        }
      />
    </Form>
  );
};

export default OrderEdit;
